import { createApiClient, apiClient } from "../../../api/apiClient";
import {
  setLoading,
  setError,
  setLeadershipWalks,
} from "../../../store/slices/leaderShipWalkSlice";

interface FetchLeadershipWalkDataParams {
  setIsLoading: (isLoading: boolean) => void;
  setApiError: (error: string | null) => void;
  dispatch: any;
  selectedLocation?: {
    id: string;
    name?: string;
    displayText?: string;
  } | null;
  selectedFilters?: {
    zone?: string;
    frequency?: string;
    createdBy?: string;
    modifiedBy?: string;
    status?: string;
  };
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface LeadershipWalkRecord {
  id: number;
  ID: number; // Keep both for backward compatibility
  Walk_Number: number;
  Title: string;
  Notes: string | null;
  Location_Name: string;
  Location_Name_Loc_ID: string | null;
  Walk_Type: string;
  Zone: string;
  Frequency: string;
  Created: string;
  Period: number;
  Week: number;
  SPARC_Category_Selected: string | null;
  Modified: string;
  Created_By: string;
  Modified_By: string | null;
  status: string;
  action?: string;
}

export const fetchLeadershipWalkData = async ({
  setIsLoading,
  setApiError,
  dispatch,
  selectedLocation,
  selectedFilters = {},
  page = 1,
  pageSize = 100,
  sortBy = "created",
  sortOrder = "desc",
}: FetchLeadershipWalkDataParams): Promise<LeadershipWalkRecord[]> => {
  try {
    setIsLoading(true);
    dispatch(setLoading(true));
    setApiError(null);

    if (!selectedLocation) {
      throw new Error("No location selected");
    }

    // Get location name, handling both formats (appData and uiPreferences)
    const locationName = selectedLocation.name || selectedLocation.displayText;
    if (!locationName) {
      throw new Error("Invalid location format");
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("location", locationName);
    params.append("walkType", "Leadership Walk");

    // Add filters if they are specified and not empty
    if (selectedFilters.zone && selectedFilters.zone !== "") {
      params.append("zone", selectedFilters.zone);
    }
    if (selectedFilters.frequency && selectedFilters.frequency !== "") {
      params.append("frequency", selectedFilters.frequency);
    }
    if (selectedFilters.createdBy && selectedFilters.createdBy !== "") {
      params.append("createdBy", selectedFilters.createdBy);
    }
    if (selectedFilters.modifiedBy && selectedFilters.modifiedBy !== "") {
      params.append("modifiedBy", selectedFilters.modifiedBy);
    }
    if (selectedFilters.status && selectedFilters.status !== "") {
      params.append("status", selectedFilters.status);
    }

    // Add pagination and sorting parameters
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);

    console.log("API request parameters:", Object.fromEntries(params));

    // Make API call
    const api = createApiClient();
    const response = await apiClient(
      api,
      `/leadership-walks?${params.toString()}`,
      "GET"
    );

    // Process response data
    if (response && Array.isArray(response)) {
      dispatch(setLeadershipWalks(response));
      return response as LeadershipWalkRecord[];
    } else {
      throw new Error("Invalid response format");
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while fetching data";
    console.error("Error fetching leadership walk data:", errorMessage);
    setApiError(errorMessage);
    dispatch(setError(errorMessage));
    return [];
  } finally {
    setIsLoading(false);
    dispatch(setLoading(false));
  }
};
